import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnChanges,
  OnInit,
  input,
  output
} from '@angular/core';

import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Attachment } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

import {
  ImmomioDropdown,
  ImmomioDropdownTrigger,
  ImmomioDropdownMenu,
  ImmomioDropdownGroup,
  ImmomioDropdownItem
} from '@immomio/ui/dropdown';
import { ImmomioIconButtonComponent } from '@immomio/ui/button';
import { FileSizePipe } from '../../../../pipes/file-size.pipe';
import { SimpleTextInputComponent } from '../../form/controls/simple-text-input/simple-text-input.component';
import { mixinAttachmentHelper } from '../../attachment/attachment-component-helper.mixin';
import { ModalService } from '../../modal/modal.service';
import { checkExifOrientation } from '../../../../utils/check-exif-orientation';
import { EditAttachmentComponent } from '../../attachment/edit-attachment/edit-attachment.component';

class AttachmentItemComponentBase {}

const _AttachmentItemComponentMixinBase = mixinAttachmentHelper(
  AttachmentItemComponentBase
);

@Component({
  selector: 'app-attachment-item-v2',
  templateUrl: './attachment-item-v2.component.html',
  styleUrls: ['./attachment-item-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImmomioDropdown,
    ImmomioDropdownTrigger,
    ImmomioDropdownMenu,
    ImmomioDropdownGroup,
    ImmomioDropdownItem,
    ImmomioIconButtonComponent,
    SimpleTextInputComponent,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslatePipe,
    FileSizePipe
  ]
})
export class AttachmentItemV2Component
  extends _AttachmentItemComponentMixinBase
  implements OnChanges, OnInit
{
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);

  public readonly attachment = input<Attachment>();
  public readonly index = input<number>(undefined);
  public readonly showDownload = input(true);
  public readonly disableDownload = input(false);
  public readonly blockDownload = input(false);
  public readonly showRemove = input(false);
  public readonly isDocument = input(false);
  public readonly orderable = input(false);
  public readonly editable = input(false);
  public readonly directDownload = input(false);

  public readonly remove = output<number>();
  public readonly download = output<Attachment>();
  public readonly preview = output<Attachment>();
  public readonly moveUp = output<Attachment>();
  public readonly moveDown = output<Attachment>();
  public progress: number;
  public isLoading: boolean;
  public imgSrc: SafeUrl | string;

  public hideIcons = true;

  public hasExifMetadata = false;

  public nameControl = new FormControl('');

  @HostListener('mouseenter')
  public onEnter() {
    this.hideIcons = false;
  }

  @HostListener('mouseleave')
  public onLeave() {
    this.hideIcons = true;
  }

  get isPDF() {
    return this.attachment().extension === 'pdf';
  }

  get getFileExtension() {
    return this.attachment()?.extension?.toUpperCase();
  }

  public ngOnInit() {
    this.nameControl.patchValue(this.name);

    checkExifOrientation(this.attachment().file, orientation => {
      this.hasExifMetadata = orientation > -1;
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges() {
    if (!this.isDocument()) {
      const attachment = this.attachment();
      this.imgSrc = this.hasFile
        ? this.getSafeUrl(attachment.file || (attachment as Blob))
        : this.attachment().url;
    }
  }

  public isPdf(attachment: Attachment) {
    return (
      attachment.type === 'PDF' ||
      (attachment.title && attachment.title.toLowerCase().endsWith('.pdf'))
    );
  }

  public onRemove() {
    this.remove.emit(this.index());
  }

  public onDownload() {
    this.download.emit(this.attachment());
  }

  public onPreview() {
    this.preview.emit(this.attachment());
  }

  public getSafeUrl(file: Blob) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  public onMoveUp() {
    if (this.orderable()) {
      this.moveUp.emit(this.attachment());
    }
  }

  public onMoveDown() {
    if (this.orderable()) {
      this.moveDown.emit(this.attachment());
    }
  }

  public editAttachment() {
    this.modalService
      .open(EditAttachmentComponent, {
        data: {
          attachment: { ...this.attachment() },
          removeFn: () => this.remove.emit(this.index())
        }
      })
      .onClose()
      .subscribe((result: Attachment) => {
        const attachment = this.attachment();
        attachment.title = result.title || '';
        this.nameControl.patchValue(attachment.title);

        attachment.rotate = result.rotate || 0;
        attachment.file = attachment.file || result.file;
      });
  }

  public action() {
    if (!this.disableDownload() && this.attachment().url) {
      this.onPreview();
    }
    if (this.directDownload()) {
      this.onDownload();
    }
  }
}
